import { z } from 'zod';
import { Uuid } from './blocks.js';
import { SignAssetUploadRequest } from './assets.js';
export const MacStatusResponse = z
    .object({
    status: z.literal('connected'),
    apiVersion: z.literal('v1'),
    tokenLabel: z.string().trim().min(1),
    serverTime: z.iso.datetime(),
    release: z
        .object({
        version: z.string().min(1),
        commit: z.string().regex(/^[0-9a-f]{40}$/),
    })
        .strict(),
})
    .strict();
export const MacGalleryCategory = z
    .object({
    slug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    title: z.string().trim().min(1).max(80),
})
    .strict();
export const MacGalleryOptionsResponse = z
    .object({ categories: z.array(MacGalleryCategory) })
    .strict();
const MacGalleryMetadataBase = z.object({
    name: z.string().trim().min(1).max(160),
    slug: z
        .string()
        .trim()
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    shootDate: z.iso.datetime(),
    categorySlug: z
        .string()
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
        .nullable(),
    location: z.string().trim().min(1).max(160).nullable(),
    websiteDescription: z.string().trim().min(1).max(500),
    visibleDescription: z.string().trim().min(1).max(2_000).optional(),
    seoTitle: z.string().trim().min(1).max(160).optional(),
    metaDescription: z.string().trim().min(1).max(500).optional(),
});
/** Request metadata. websiteTitle is accepted temporarily for older Mac builds and ignored. */
export const MacGalleryMetadataInput = MacGalleryMetadataBase.extend({
    websiteTitle: z.string().trim().min(1).max(240).optional(),
}).strict();
/** Response metadata keeps the derived websiteTitle while older Mac builds still decode it. */
export const MacGalleryMetadata = MacGalleryMetadataBase.extend({
    websiteTitle: z.string().trim().min(1).max(240),
}).strict();
export const MacUpsertGalleryRequest = z
    .object({
    expectedRevision: z.number().int().nonnegative().nullable(),
    metadata: MacGalleryMetadataInput,
})
    .strict();
export const MacGallery = z
    .object({
    id: Uuid,
    clientGalleryId: Uuid,
    shootId: Uuid.nullable(),
    revision: z.number().int().nonnegative(),
    metadata: MacGalleryMetadata,
    lastSyncedAt: z.iso.datetime().nullable(),
})
    .strict();
export const MacGalleryResponse = z.object({ gallery: MacGallery }).strict();
export const MacGalleryPhotoVariant = z
    .object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    byteSize: z.number().int().positive(),
    checksum: z.string().regex(/^[0-9a-f]{64}$/),
    assetId: Uuid,
})
    .strict();
export const MacGalleryPhoto = z
    .object({
    clientPhotoId: z.string().regex(/^[0-9a-f]{64}$/),
    filename: z.string().trim().min(1).max(255),
    alt: z.string().trim().min(1).max(500),
    position: z.number().int().nonnegative(),
    pinterest: z.boolean(),
    sourceFingerprint: z.string().trim().min(1).max(255),
    recipeVersion: z.string().trim().min(1).max(80),
    variants: z.array(MacGalleryPhotoVariant).min(1).max(3),
})
    .strict();
export const MacSyncGalleryContentRequest = z
    .object({
    expectedRevision: z.number().int().nonnegative(),
    idempotencyKey: Uuid,
    coverClientPhotoId: z.string().regex(/^[0-9a-f]{64}$/),
    photos: z.array(MacGalleryPhoto).min(1).max(500),
})
    .strict();
export const MacGallerySyncResponse = z
    .object({
    gallery: MacGallery,
    photoCount: z.number().int().positive(),
    assetCount: z.number().int().positive(),
    idempotent: z.boolean(),
})
    .strict();
export const MacSignAssetUploadRequest = SignAssetUploadRequest;
export const MacGalleryRevisionConflictResponse = z
    .object({
    error: z
        .object({
        code: z.literal('REVISION_CONFLICT'),
        message: z.string().min(1),
        expectedRevision: z.number().int().nonnegative().nullable(),
        currentRevision: z.number().int().nonnegative(),
        current: MacGallery,
    })
        .strict(),
})
    .strict();
export const MacGallerySlugConflictResponse = z
    .object({
    error: z
        .object({
        code: z.literal('GALLERY_SLUG_CONFLICT'),
        message: z.string().min(1),
        reconnectAllowed: z.boolean(),
        existing: MacGallery.nullable(),
    })
        .strict(),
})
    .strict()
    .superRefine((response, context) => {
    if (response.error.reconnectAllowed !== (response.error.existing !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['error', 'reconnectAllowed'],
            message: 'Reconnect is allowed only when an existing Mac Gallery is returned',
        });
    }
});
export const GalleryEnrichmentState = z.enum([
    'WaitingForConnection',
    'AwaitingPreviews',
    'Uploading',
    'Queued',
    'Analyzing',
    'Drafting',
    'Ready',
    'ReadyWithWarnings',
    'Failed',
    'ResourceLimitExceeded',
    'Canceled',
    'Superseded',
]);
export const GalleryEnrichmentError = z
    .object({
    code: z.string().trim().min(1).max(80),
    message: z.string().trim().min(1).max(1_000),
    retryable: z.boolean(),
})
    .strict();
export const GalleryEnrichmentPreviewManifestItem = z
    .object({
    photoId: z.string().regex(/^[0-9a-f]{64}$/),
    filename: z.string().trim().min(1).max(255),
    previewChecksum: z.string().regex(/^[0-9a-f]{64}$/),
    byteSize: z
        .number()
        .int()
        .positive()
        .max(8 * 1024 * 1024),
    width: z.number().int().positive().max(512),
    height: z.number().int().positive().max(512),
    recipeVersion: z.string().trim().min(1).max(80),
})
    .strict();
export const CreateGalleryEnrichmentJobRequest = z
    .object({
    idempotencyKey: Uuid,
    manifestHash: z.string().regex(/^[0-9a-f]{64}$/),
    confirmedCategorySlug: z
        .string()
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
        .nullable(),
    photos: z.array(GalleryEnrichmentPreviewManifestItem).min(1).max(500),
})
    .strict();
export const GalleryEnrichmentPhotoSuggestion = z
    .object({
    photoId: z.string().regex(/^[0-9a-f]{64}$/),
    previewChecksum: z.string().regex(/^[0-9a-f]{64}$/),
    visualSummary: z.string().trim().min(1).max(2_000),
    altText: z.string().trim().min(1).max(500),
    confidence: z.number().min(0).max(1),
    flags: z.array(z.string().trim().min(1).max(80)).max(20),
    engine: z.literal('hermes-vision'),
    runtime: z.literal('hermes-vision:auto'),
    promptVersion: z.string().trim().min(1).max(80),
    schemaVersion: z.string().trim().min(1).max(80),
    runId: z.string().trim().min(1).max(160).nullable(),
    durationMilliseconds: z.number().int().nonnegative().nullable(),
})
    .strict();
export const GalleryEnrichmentCopySuggestion = z
    .object({
    visibleDescription: z.string().trim().min(1).max(2_000),
    seoTitle: z.string().trim().min(1).max(160),
    metaDescription: z.string().trim().min(1).max(500),
    suggestedRelatedServiceRoute: z.string().startsWith('/').max(255).nullable(),
    warnings: z.array(z.string().trim().min(1).max(200)).max(20),
    engine: z.literal('hermes-vision'),
    runtime: z.literal('hermes-vision:auto'),
    promptVersion: z.string().trim().min(1).max(80),
    schemaVersion: z.string().trim().min(1).max(80),
    runId: z.string().trim().min(1).max(160).nullable(),
    durationMilliseconds: z.number().int().nonnegative().nullable(),
})
    .strict();
const GalleryEnrichmentCallbackCopySuggestion = GalleryEnrichmentCopySuggestion.omit({
    engine: true,
    runtime: true,
    seoTitle: true,
    metaDescription: true,
})
    .extend({
    // Hermes intentionally generates only visibleDescription. These legacy
    // fields are accepted but never authoritative; Diana derives both values.
    seoTitle: z.string().trim().min(1).max(160).nullable().optional(),
    metaDescription: z.string().trim().min(1).max(500).nullable().optional(),
    engine: z.string().optional(),
    runtime: z.string().optional(),
})
    .strict();
export const GalleryEnrichmentJob = z
    .object({
    jobId: Uuid,
    galleryId: Uuid,
    manifestHash: z.string().regex(/^[0-9a-f]{64}$/),
    state: GalleryEnrichmentState,
    stage: z.string().trim().min(1).max(80).nullable(),
    attempt: z.number().int().positive(),
    confirmedCategorySlug: z
        .string()
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
        .nullable(),
    expectedPhotoCount: z.number().int().positive(),
    uploadedPhotoCount: z.number().int().nonnegative(),
    analyzedPhotoCount: z.number().int().nonnegative(),
    failedPhotoCount: z.number().int().nonnegative(),
    error: GalleryEnrichmentError.nullable(),
    photoResults: z.array(GalleryEnrichmentPhotoSuggestion),
    galleryResult: GalleryEnrichmentCopySuggestion.nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    completedAt: z.iso.datetime().nullable(),
})
    .strict();
export const GalleryEnrichmentJobResponse = z
    .object({ job: GalleryEnrichmentJob, idempotent: z.boolean().optional() })
    .strict();
export const CreateGalleryEnrichmentUploadSessionRequest = z
    .object({
    photoIds: z
        .array(z.string().regex(/^[0-9a-f]{64}$/))
        .min(1)
        .max(100),
})
    .strict();
export const GalleryEnrichmentUploadTarget = z
    .object({
    photoId: z.string().regex(/^[0-9a-f]{64}$/),
    uploadUrl: z.url(),
    requiredHeaders: z.record(z.string(), z.string()),
})
    .strict();
export const GalleryEnrichmentUploadSessionResponse = z
    .object({
    sessionId: Uuid,
    expiresAt: z.iso.datetime(),
    uploads: z.array(GalleryEnrichmentUploadTarget).min(1).max(100),
})
    .strict();
export const CompleteGalleryEnrichmentUploadSessionRequest = z.object({ sessionId: Uuid }).strict();
export const GalleryEnrichmentJobCallback = z
    .object({
    jobId: Uuid,
    manifestHash: z.string().regex(/^[0-9a-f]{64}$/),
    state: GalleryEnrichmentState,
    stage: z.string().trim().min(1).max(80).nullable(),
    uploadedPhotoCount: z.number().int().nonnegative(),
    analyzedPhotoCount: z.number().int().nonnegative(),
    failedPhotoCount: z.number().int().nonnegative(),
    error: GalleryEnrichmentError.nullable(),
    photoResults: z.array(GalleryEnrichmentPhotoSuggestion.omit({
        engine: true,
        runtime: true,
    }).extend({
        engine: z.string().optional(),
        runtime: z.string().optional(),
    })),
    galleryResult: GalleryEnrichmentCallbackCopySuggestion.nullable(),
})
    .strict();
